import { computed, inject, Injectable, resource } from '@angular/core';
import { StockPostingItemRepository } from '../repositories/stock-posting-item.repository';
import {
  StockPostingItem,
  StockPostingItemPatch,
  StockPostingItemPost,
} from '../types/stock-posting-item';
import { StockPostingService } from './stock-posting.service';
import { isDefinedNotEmpty } from '@softline/core';

/** Items of the open stock posting; mutations update the collection in place. */
@Injectable()
export class StockPostingItemService {
  private readonly repository = inject(StockPostingItemRepository);
  private readonly stockPostingService = inject(StockPostingService);

  private readonly itemsResource = resource<StockPostingItem[], number | undefined>({
    params: () => this.stockPostingService.stockPosting()?.id,
    loader: ({ params: stockPostingId }) => this.repository.load(stockPostingId),
    defaultValue: [],
  });

  readonly items = computed<StockPostingItem[]>(() =>
    this.itemsResource.hasValue() ? this.itemsResource.value() : []
  );
  readonly isLoading = this.itemsResource.isLoading;
  readonly error = this.itemsResource.error;

  reload(): void {
    this.itemsResource.reload();
  }

  async addItem(body: StockPostingItemPost): Promise<StockPostingItem> {
    const item = await this.repository.create(this.requireId(), body);
    const id = this.stockPostingService.stockPosting()?.id;

    if (isDefinedNotEmpty(id)) {
      const items = await this.repository.load(id);
      this.itemsResource.set(items);
    }

    return item;
  }

  async updateItem(
    itemId: number,
    patch: Partial<StockPostingItemPatch>
  ): Promise<StockPostingItem> {
    const updated = await this.repository.patch(this.requireId(), itemId, patch);
    this.itemsResource.set(
      this.items().map((item) => (item.id === itemId ? updated : item))
    );
    return updated;
  }

  async deleteItem(itemId: number): Promise<void> {
    await this.repository.delete(this.requireId(), itemId);
    this.itemsResource.set(this.items().filter((item) => item.id !== itemId));
  }

  private requireId(): number {
    const id = this.stockPostingService.stockPosting()?.id;
    if (id === undefined) throw new Error('No open stock posting');
    return id;
  }
}
